import { ComponentType } from '@angular/cdk/portal';
import {
  ComponentRef,
  Inject,
  Injectable,
  Injector,
  NgModuleFactory,
  NgModuleFactoryLoader,
  Optional,
  SkipSelf,
  ViewContainerRef,
} from '@angular/core';
import { ROUTES, Routes } from '@angular/router';
import { ModalSize } from '@freelancer/ui/modal';
import * as Rx from 'rxjs';

/**
 * Custom injector to be used when providing custom
 * injection tokens to components inside a portal.
 */
export class PortalInjector implements Injector {
  constructor(
    private _parentInjector: Injector,
    private _customTokens: WeakMap<any, any>,
  ) {}

  get(token: any, notFoundValue?: any): any {
    const value = this._customTokens.get(token);

    if (typeof value !== 'undefined') {
      return value;
    }

    return this._parentInjector.get<any>(token, notFoundValue);
  }
}

export interface ModalConfig {
  inputs?: Object; // the @Input() values to pass to the component
  size?: ModalSize;
  hideCloseButton?: boolean;
  showBackdrop?: boolean; // TODO: remove once webapp modals created: T46290
  closeOnBackdropClick?: boolean;
  isDrawer?: boolean; // full height drawer
  edgeToEdge?: boolean;
}

export type ModalResult = any;

export class ModalRef<T> {
  /** Subject that emits when the modal finishes opening. */
  private _afterOpenSubject$ = new Rx.Subject<void>();
  /** Subject that emits after the modal finishes closing and returns a result */
  private _afterClosedSubject$: Rx.Subject<ModalResult> = new Rx.Subject();
  private componentRef: ComponentRef<T>;

  constructor(private _openStreamSubject$: Rx.Subject<any>) {}

  set(componentRef: ComponentRef<T>) {
    this.componentRef = componentRef;
    this._afterOpenSubject$.next();
    this._afterOpenSubject$.complete();
  }

  close(result?: ModalResult): void {
    this.componentRef.destroy();
    this._openStreamSubject$.next(false);
    this._afterClosedSubject$.next(result);
    this._afterClosedSubject$.complete();
  }

  afterOpen(): Rx.Observable<void> {
    return this._afterOpenSubject$.asObservable();
  }

  afterClosed(): Rx.Observable<ModalResult> {
    return this._afterClosedSubject$.asObservable();
  }
}

@Injectable()
export class ModalService {
  private _openStreamSubject$ = new Rx.Subject<any>();
  openStream$ = this._openStreamSubject$.asObservable();

  // rendering container
  private injector: Injector;
  private viewContainerRef: ViewContainerRef;
  private openModalRef?: ModalRef<any>;
  private modalConfig: ModalConfig;

  constructor(
    private loader: NgModuleFactoryLoader,
    @Inject(ROUTES) private readonly loadableRoutes: Routes[],
  ) {}

  close() {
    if (this.openModalRef) {
      this.openModalRef.close();
      this.openModalRef = undefined;
    }
  }

  open<T>(
    component: ComponentType<T>,
    modalConfig: ModalConfig,
  ): ModalRef<any> {
    const ngModule = this.getModalNgModule(component);
    this.modalConfig = modalConfig;
    this._openStreamSubject$.next(
      modalConfig.showBackdrop !== undefined ? modalConfig.showBackdrop : true,
    );

    const modalRef = new ModalRef(this._openStreamSubject$);
    this.openModalRef = modalRef;
    // asynchronously set the componentRef in the modal
    // we return immediately to make the API nicer.
    this.loader.load(ngModule).then((ngModuleFactory: NgModuleFactory<any>) => {
      // get component factory
      const componentFactory = ngModuleFactory
        .create(this.injector) // ngModuleRef
        .componentFactoryResolver.resolveComponentFactory(component);

      // create custom injector
      const injectionTokens = new WeakMap();
      injectionTokens.set(ModalRef, modalRef);
      const customInjector = new PortalInjector(this.injector, injectionTokens);

      // create component
      const componentRef = this.viewContainerRef.createComponent(
        componentFactory,
        0,
        customInjector,
      );
      if (modalConfig.inputs) {
        Object.entries(modalConfig.inputs).forEach(([key, value]) => {
          // FIXME: ComponentType<T> has no index signature.
          (<any>componentRef).instance[key] = value;
        });
      }
      modalRef.set(componentRef);
    });
    return modalRef;
  }

  setContainer(viewContainerRef: ViewContainerRef, injector: Injector) {
    this.viewContainerRef = viewContainerRef;
    this.injector = injector;
  }

  getCurrentConfig() {
    return this.modalConfig;
  }

  private getModalNgModule<T>(component: ComponentType<T>): string {
    const componentRoute = this.loadableRoutes
      .reduce((acc, list) => [...acc, ...list], []) // flatten
      .find(route => route.component === component); // find component
    if (componentRoute && typeof componentRoute.loadChildren === 'string') {
      return componentRoute.loadChildren;
    }
    throw new Error(`missing modal module configuration for ${component.name}`);
  }
}

/**
 * Provider factory for Modal Service
 *
 * @export
 * @param {ModalService} parentRegistry The parent registry
 * @returns {ModalService} The singleton instance or a new one
 */
export function MODAL_SERVICE_PROVIDER_FACTORY(
  parentRegistry: ModalService,
  loader: NgModuleFactoryLoader,
  loadableRoutes: Routes[],
) {
  return parentRegistry || new ModalService(loader, loadableRoutes);
}

/**
 * Create a service provider that acts as a singleton.
 */
export const MODAL_SERVICE_PROVIDER = {
  // If there is already an ModalService available, use that.
  // Otherwise, provide a new one.
  provide: ModalService,
  deps: [
    [new Optional(), new SkipSelf(), ModalService],
    NgModuleFactoryLoader,
    ROUTES,
  ],
  useFactory: MODAL_SERVICE_PROVIDER_FACTORY,
};
